import { Back, Power1, TweenMax } from 'gsap';
import { GameAudio } from '../Config/Audio';
import { BOOSTER_LIST, ItemIDs } from '../Config/Items';
import { getGameSize, IGameSizeInfo } from '../Config/Levels';
import Core, { GameOrientation } from '../Core/Core';
// import getLogger, { ILogger } from '../Core/Logger';
import { TILE_RATIO } from '../Game/BoardSizing';
import { Pair } from '../Game/BoardUtils';
import { gameConfig } from '../index';
import { Hud } from '../Managers/Hud/Hud';
import { IBoosterConfig } from '../Managers/Level/Level';
import SoundManager from '../Sound/SoundManager';
import { StuckReasons } from '../State/reducers/level';
import { IRewardItem } from '../State/reducers/score';
import { Collection } from '../Tiles/TileSet';
import { Animation } from '../UI/Animations/Animation';
import { showFirecracker } from '../UI/Animations/Boosters/BoosterFirecracker';
import { showHighlightBooster } from '../UI/Animations/Boosters/BoosterHighlight';
import { showHint } from '../UI/Animations/Boosters/BoosterHint';
import { BoosterTime } from '../UI/Animations/Boosters/BoosterTime';
import { SkippableAnimation } from '../UI/Animations/SkippableAnimation';
import { BoardUI } from '../UI/Game/Board/BoardUI';
import { GoalBanner } from '../UI/Game/Board/GoalBanner';
import { FailflowPopup } from '../UI/PopUps/FailflowPopup';
import { WinLevelPopup } from '../UI/PopUps/Win/WinLevelPopup';
import { ScreenBlur } from '../UI/Shared/ScreenBlur';
import { ScreenOverlay } from '../UI/Shared/ScreenOverlay';
import { waitForMs } from '../Utils/AsyncUtils';
import GameLoader from '../Core/Assets';

// const logger: ILogger = getLogger('[LEVELVIEW]:');

export default class LevelView extends PIXI.Container {
    public board: BoardUI;
    public hud: Hud;
    public gameOverlay: ScreenOverlay;
    private gameBg: PIXI.Sprite;
    private boardSize: Pair;
    private activeCollection: Collection;
    private win: WinLevelPopup;
    private stuckPopup: FailflowPopup;
    private screenBlur: ScreenBlur;
    private highightOutAnim: Animation;
    private winPopup: WinLevelPopup;

    constructor(boardSize: [Pair, Pair], hud: Hud, activeCollection: Collection) {
        super();

        // area shown for all screens
        const safeSize: [number, number] = [gameConfig.game_width, gameConfig.game_height];

        this.activeCollection = activeCollection;
        this.gameBg = this.createBg(safeSize);
        this.board = this.createBoardUI(safeSize, boardSize, hud);
        this.boardSize = safeSize;
        this.hud = hud;
        //@ts-ignore
        this.addChild(this.gameBg, this.board);

        this.startLevelMusic();
    }

    public resize(sizeInfo: IGameSizeInfo) {
        this.gameBg.scale.set(1);
        this.gameBg.scale.set(sizeInfo.gameWidth / this.gameBg.width);
        if (this.gameBg.height < sizeInfo.gameHeight) {
            this.gameBg.scale.set(1);
            this.gameBg.scale.set(sizeInfo.gameHeight / this.gameBg.height);
        }

        this.gameBg.x = (sizeInfo.gameWidth - this.gameBg.width) / 2;
        this.gameBg.y = (sizeInfo.gameHeight - this.gameBg.height) / 2;

        const isPortrait = Core.orientation === GameOrientation.PORTRAIT;
        const w: number = isPortrait ? this.boardSize[0] * 0.9 : this.boardSize[0];
        const h: number = w / TILE_RATIO;
        const shiftX = isPortrait ? w * 0.05 : 0;
        this.board.x = getGameSize().xOffset + shiftX;
        this.board.y = getGameSize().yOffset + (this.boardSize[1] - h) / 2; // shift up for bottom ui

        if (this.win) {
            this.win.resize(sizeInfo);
        }
        if (this.stuckPopup) {
            this.stuckPopup.resize(sizeInfo);
        }
        if (this.screenBlur) {
            this.screenBlur.resize(sizeInfo);
        }
    }

    /**
     * Add a game booster
     * @param id - booster id
     * @param config - config for booster animation, [ id, position, delay ]
     */
    public async createBooster(config: IBoosterConfig) {
        this.screenOverlay(true, 300);

        if (config.id === ItemIDs.HIGHLIGHT) {
            this.highightOutAnim = await showHighlightBooster(
                config.loc as PIXI.Point,
                this.board.dimAllTiles.bind(this.board),
                this.board.revealAvailable.bind(this.board),
                this
            );
            const isPortrait = Core.orientation === GameOrientation.PORTRAIT;
            if (isPortrait) {
                await waitForMs(200);
                await this.highightOutAnim.play();
                this.highightOutAnim = null;
            }
            this.screenOverlay(false, 300);
            return this.highightOutAnim;
        }

        if (config.id === ItemIDs.FIRECRACKER) {
            return showFirecracker(config.delay, config.loc, this.board.boardTileSize, this);
        }

        if (config.id === ItemIDs.HINT) {
            return showHint(config.loc, this).then(() => {
                this.screenOverlay(false, 300);
            });
        }

        if (config.id === ItemIDs.MAGNET) {
            return new Promise(async resolve => {
                // this.screenOverlay(false, 300);
                await waitForMs(300);
                resolve();
            });
        }

        return new Promise(resolve => {
            resolve();
        });
    }

    public async showLevelGoal(goalId: string, collection: Collection) {
        const goalBanner = new GoalBanner(goalId, collection);
        goalBanner.x = getGameSize().gameWidth / 2;
        goalBanner.y = getGameSize().gameHeight / 2;
        goalBanner.scale.set(0, 1);
        goalBanner.alpha = 0;
        const sBlur = new ScreenBlur();
        this.screenBlur = sBlur;
        sBlur.alpha = 0;
        //@ts-ignore
        this.addChild(sBlur, goalBanner);
        SoundManager.playSound(GameAudio.WIN_POPUP);
        const animation = new SkippableAnimation();
        animation.add(
            TweenMax.to([sBlur, goalBanner], 0.4, {
                alpha: 1,
            })
        );
        animation.addToLast(
            TweenMax.to(goalBanner.scale, 0.4, {
                x: 1,
                ease: Back.easeOut,
            })
        );
        animation.addDelay(1200);
        animation.add(
            TweenMax.to([sBlur, goalBanner], 0.4, {
                alpha: 0,
            })
        );
        animation.add(() => {
            sBlur.destroyBlur();
            this.screenBlur = null;
            goalBanner.destroy();
        });
        await animation.play();
    }

    public completeHints() {
        this.board.stopHints();
    }

    public async animateMagnet() {
        return new Promise(async resolve => {
            SoundManager.playSound(GameAudio.MAGNET_START);
            await waitForMs(1200);
            SoundManager.playSound(GameAudio.MAGNET_END);
            this.screenOverlay(false, 300);
            await waitForMs(300);
            resolve();
        });
    }

    public screenOverlay(fadeIn: boolean, time: number) {
        const overlay = this.gameOverlay || new ScreenOverlay();
        overlay.alpha = fadeIn ? 0 : 1;
        const target = fadeIn ? 1 : 0;
        this.addChildAt(overlay, 1);
        overlay.fade(target, time);

        if (fadeIn) {
            this.gameOverlay = overlay;
        } else {
            this.gameOverlay = null;
        }
    }

    public showStuckPopup(reason: StuckReasons, isStuckTutorial: boolean = false) {
        SoundManager.stopAllMusic();
        SoundManager.playSound(GameAudio.STUCK_JINGLE);
        const popup = new FailflowPopup();
        const boosterList = reason === StuckReasons.TIME ? [ItemIDs.TIME] : BOOSTER_LIST;
        const attentionList =
            isStuckTutorial && reason !== StuckReasons.TIME ? [ItemIDs.SHUFFLE] : boosterList;

        if (this.hud.ui && this.hud.ui.bottomUi) {
            this.hud.ui.bottomUi.boosters.setCallAttention(true, attentionList);
        }

        this.stuckPopup = popup;
        this.addChild(popup);
        popup.tweenIn();
    }

    public hideStuckPopup(hide: boolean = true) {
        if (!this.stuckPopup) {
            return;
        }
        if (hide) {
            this.stuckPopup.interactive = false;
            this.stuckPopup.alpha = 0;
        } else {
            this.stuckPopup.interactive = true;
            this.stuckPopup.alpha = 1;
        }
    }

    public closeStuckPopup() {
        if (!this.stuckPopup) {
            return;
        }
        SoundManager.resumeAllMusic();
        this.stuckPopup.tweenOut();

        if (this.hud.ui && this.hud.ui.bottomUi) {
            this.hud.ui.bottomUi.boosters.setCallAttention(false, BOOSTER_LIST);
        }

        this.stuckPopup = null;
    }

    public async failFlowAnimation() {
        return this.board.failFlowAnimation();
    }

    public async highlightOutAnimation() {
        if (!this.highightOutAnim) {
            return Promise.resolve();
        }

        return this.highightOutAnim.play();
    }

    public showShuffle() {
        SoundManager.playSound(GameAudio.SHUFFLE_SOUND);
        const ferret = new PIXI.Sprite(PIXI.utils.TextureCache['loading/spin-ferret']);
        ferret.anchor.set(0.5);
        ferret.scale.set(2.2);
        ferret.x = getGameSize().gameWidth * 0.5;
        ferret.y = getGameSize().gameHeight + getGameSize().gameHeight * 0.1;

        this.addChildAt(ferret, this.getChildIndex(this.board) + 1);
        TweenMax.to(ferret, 2, {
            y: getGameSize().gameHeight * 0.3,
            ease: Power1.easeInOut,
            rotation: 40,
        });
        TweenMax.to(ferret, 1, {
            y: getGameSize().gameHeight * 0.3,
            ease: Power1.easeInOut,
            repeat: 1,
            yoyo: true,
            onComplete: () => {
                ferret.destroy();
                // this.screenOverlay(true, 300);
            },
        });

        setTimeout(() => {
            SoundManager.playSound(GameAudio.FERRET_YAHOO);
        }, 500);
    }

    public async showTime() {
        return new Promise(resolve => {
            const boosterAnimation = new BoosterTime(1);
            const boosterSprite = boosterAnimation.sprite;
            boosterSprite.x = getGameSize().gameWidth * 0.5;
            boosterSprite.y = getGameSize().gameHeight * 0.5;
            this.addChildAt(boosterSprite, this.getChildIndex(this.board) + 1);
            boosterAnimation.play(resolve);
        });
    }

    public async showWin(
        rewards: IRewardItem[],
        score: number,
        replay: boolean,
        collectableInProgress: string,
        collectableProgress: number
    ): Promise<void> {
        const win = new WinLevelPopup(
            rewards,
            score,
            replay,
            collectableInProgress,
            collectableProgress
        );
        this.addChild(win);

        this.winPopup = win;
        await win.tweenIn();
    }

    public removeWin() {
        if (this.winPopup) {
            this.winPopup.tweenOut();
            this.win = null;
        }
    }

    public async playMagnetPullSound(wTime: number) {
        await waitForMs(wTime);
        SoundManager.playSound(GameAudio.MAGNET_MOVE);
    }

    public cleanUp() {
        SoundManager.stopSound('game-music', true, 1000);
        this.gameBg.destroy();
        this.removeAllListeners();
        this.destroy();
    }

    private createBg(size: Pair): PIXI.Sprite {
        const levelIdString = Core.store.getState().StateLevel.levelId;
        const split = levelIdString.split('-');
        const levelId = parseInt(split[1], 10);
        let n = 1;
        if (levelId < 18) {
            n = 1;
        } else if (levelId < 35) {
            n = 2;
        } else {
            n = 3;
        }
        const bg = new PIXI.Sprite(PIXI.utils.TextureCache['gameBg-' + n]);
        bg.x = (size[0] - bg.width) / 2;
        bg.y = (size[1] - bg.height) / 2;
        return bg;
    }

    /**
     *
     * @param screenSize - screen area for all screens in pixls [width , height]
     * @param boardSize - board dimensions in tile units [w, h], [xoffset, yoffset]
     * @param hud - reference to hud used for item and reward positioning
     */
    private createBoardUI(screenSize: Pair, boardSize: [Pair, Pair], hud: Hud): BoardUI {
        const isPortrait = Core.orientation === GameOrientation.PORTRAIT;
        const w: number = isPortrait ? screenSize[0] * 0.9 : screenSize[0];
        const h: number = w / TILE_RATIO;
        const screenPair: Pair = [w, h];
        const comp = new BoardUI(screenPair, boardSize, hud, this.activeCollection);
        // do not set position here because it is overwritten by resize function immediately

        return comp;
    }

    private async startLevelMusic() {
        // shouldn't get state here...
        await GameLoader.loadAssetList({
            assetList: [Core.store.getState().StatePlayer.mapId + '-game'],
        });
        SoundManager.playMusic('game-music', true, 1000);
    }
}
